import PropTypes from 'prop-types';
import React from 'react';

import { Container } from '@material-ui/core';

import FileUploader from '@hydra/routes/pdf-editor/components/FileUploader';
import FormDropdown from '@hydra/routes/pdf-editor/components/FormDropdown';
import FormRadioButtons from '@hydra/routes/pdf-editor/components/FormRadioButtons';
import FormToggle from '@hydra/routes/pdf-editor/components/FormToggle';

import Editor from '@hydra/routes/pdf-editor/components/EditorFormRow/Editor';
import EditorFormRow from '@hydra/routes/pdf-editor/components/EditorFormRow/EditorFormRow';
import EditorInline from '@hydra/routes/pdf-editor/components/EditorFormRow/EditorInline';
import Header from '@hydra/routes/pdf-editor/components/Header';
import HelpText from '@hydra/routes/pdf-editor/components/HelpText';
import { Translate } from '@hydra/common/components';
import FilePreview from '@hydra/routes/pdf-editor/components/FilePreview';
import GoogleDomainsDropdown from '../GoogleDomainsDropdown';

const rankFilterOptions = [
  { value: '10', content: <Translate content='pdf_editor_advanced_ranked_keywords_filter_option_label_10' /> },
  { value: '50', content: <Translate content='pdf_editor_advanced_ranked_keywords_filter_option_label_50' /> },
  { value: '100', content: <Translate content='pdf_editor_advanced_ranked_keywords_filter_option_label_100' /> }
];

const orderOptions = [
  { value: 'keyword_asc', content: <Translate content='pdf_editor_advanced_ranked_keywords_order_option_label_keyword_asc' /> },
  { value: 'keyword_desc', content: <Translate content='pdf_editor_advanced_ranked_keywords_order_option_label_keyword_desc' /> },
  { value: 'volume_asc', content: <Translate content='pdf_editor_advanced_ranked_keywords_order_option_label_volume_asc' /> },
  { value: 'volume_desc', content: <Translate content='pdf_editor_advanced_ranked_keywords_order_option_label_volume_desc' /> },
  { value: 'position_asc', content: <Translate content='pdf_editor_advanced_ranked_keywords_order_option_label_position_asc' /> },
  { value: 'position_desc', content: <Translate content='pdf_editor_advanced_ranked_keywords_order_option_label_position_desc' /> }
];

export const TabAdvanced = ({
  template,
  editing,
  editingValue,
  setEditingValue,
  setEditingField,
  resetEditing,
  saveFormField,
  savePdfFile,
  headerLocked,
  featuresLocked,
  countries
}) => {
  return (
    <>
      <Header title='pdf_editor_review_tab_advanced' hasLockedButton={headerLocked} />
      <Container>
        <EditorFormRow
          label={<Translate content='pdf_editor_advanced_custom_opening_page' />}
          help={<HelpText content='pdf_editor_option_help_advanced_custom_opening_page' />}
          locked={featuresLocked}
        >
          <Editor
            Component={FileUploader}
            field='customOpeningPage'
            preview={<FilePreview file={template.customOpeningPage} />}
            previewClickable={!template.customOpeningPage}
            fileUpload
            value={template.customOpeningPage}
            editingValue={editingValue}
            editing={editing === 'customOpeningPage'}
            otherFieldOpen={(editing !== null && editing !== 'customOpeningPage')}
            setEditingValue={setEditingValue}
            setEditingField={setEditingField}
            resetEditing={resetEditing}
            savePdfFile={savePdfFile}
            locked={featuresLocked}
          />
        </EditorFormRow>
        <EditorFormRow
          label={<Translate content='pdf_editor_advanced_custom_closing_page' />}
          help={<HelpText content='pdf_editor_option_help_advanced_custom_closing_page' />}
          locked={featuresLocked}
        >
          <Editor
            Component={FileUploader}
            field='customClosingPage'
            preview={<FilePreview file={template.customClosingPage} />}
            previewClickable={!template.customClosingPage}
            fileUpload
            value={template.customClosingPage}
            editingValue={editingValue}
            editing={editing === 'customClosingPage'}
            otherFieldOpen={(editing !== null && editing !== 'customClosingPage')}
            setEditingValue={setEditingValue}
            setEditingField={setEditingField}
            resetEditing={resetEditing}
            savePdfFile={savePdfFile}
            locked={featuresLocked}
          />
        </EditorFormRow>
        <EditorFormRow
          label={<Translate content='pdf_editor_advanced_add_ranked_keywords' />}
          locked={featuresLocked}
        >
          <EditorInline
            Component={FormToggle}
            field='rankedKeywords.enabled'
            value={template.rankedKeywords.enabled}
            otherFieldOpen={(editing !== null && editing !== 'rankedKeywords.enabled')}
            locked={featuresLocked}
            saveFormField={({ value }) => saveFormField({
              field: 'rankedKeywords',
              value: {
                ...template.rankedKeywords,
                enabled: value
              }
            })}
          />
        </EditorFormRow>
        {
          template.rankedKeywords.enabled && (
            <>
              <EditorFormRow
                label={<Translate content='pdf_editor_advanced_select_google_domain' />}
                help={<Translate content='pdf_editor_advanced_select_google_domain_options_help' />}
                locked={featuresLocked}
              >
                <EditorInline
                  Component={GoogleDomainsDropdown}
                  items={countries}
                  field='rankedKeywords.googleDomain'
                  value={template.rankedKeywords.googleDomain}
                  otherFieldOpen={(editing !== null && editing !== 'rankedKeywords.googleDomain')}
                  locked={featuresLocked}
                  saveFormField={({ value }) => saveFormField({
                    field: 'rankedKeywords',
                    value: {
                      ...template.rankedKeywords,
                      googleDomain: value
                    }
                  })}
                />
              </EditorFormRow>
              <EditorFormRow
                label={<Translate content='pdf_editor_advanced_order_ranked_keywords_by' />}
                help={<Translate content='pdf_editor_advanced_order_ranked_keywords_by_options_help' />}
                locked={featuresLocked}
              >
                <EditorInline
                  Component={FormDropdown}
                  items={orderOptions}
                  field='rankedKeywords.order'
                  value={template.rankedKeywords.order}
                  otherFieldOpen={(editing !== null && editing !== 'rankedKeywords.order')}
                  locked={featuresLocked}
                  saveFormField={({ value }) => saveFormField({
                    field: 'rankedKeywords',
                    value: {
                      ...template.rankedKeywords,
                      order: value
                    }
                  })}
                />
              </EditorFormRow>
              <EditorFormRow
                label={<Translate content='pdf_editor_advanced_ranked_keywords_filter_data' />}
                help={<Translate content='pdf_editor_advanced_ranked_keywords_filter_data_options_help' />}
                locked={featuresLocked}
              >
                <EditorInline
                  Component={FormRadioButtons}
                  items={rankFilterOptions}
                  field='rankedKeywords.rankFilter'
                  value={template.rankedKeywords.rankFilter}
                  otherFieldOpen={(editing !== null && editing !== 'rankedKeywords.rankFilter')}
                  locked={featuresLocked}
                  saveFormField={({ value }) => saveFormField({
                    field: 'rankedKeywords',
                    value: {
                      ...template.rankedKeywords,
                      rankFilter: value
                    }
                  })}
                />
              </EditorFormRow>
            </>
          )
        }
      </Container>
    </>
  );
};

TabAdvanced.propTypes = {
  template: PropTypes.shape({
    name: PropTypes.string
  })
};

export default React.memo(TabAdvanced);
